import { CREATE_CLIENT } from "@lib/graphql/mutations";
import { CLIENTS_QUERY } from "@lib/graphql/queries";
import { useChatDataVersion } from "@lib/hooks/useChatDataVersion";
import type { Connection } from "@lib/hooks/usePaginatedList";
import { usePaginatedList } from "@lib/hooks/usePaginatedList";
import type { Client } from "@lib/types";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useMutation } from "urql";

export function ClientList() {
  const {
    nodes: clients,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ clients: Connection<Client> }, Client>({
    query: CLIENTS_QUERY,
    getConnection: (data) => data.clients,
  });
  const [, createClient] = useMutation(CREATE_CLIENT);
  useChatDataVersion(refresh);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const result = await createClient({
      input: { name, industry: industry || undefined, website: website || undefined },
    });
    if (result.error) {
      setError(result.error.message || "Something went wrong. Please try again.");
      return;
    }
    const fieldErrors = result.data?.createClient?.errors ?? [];
    if (fieldErrors.length > 0) {
      setError(fieldErrors.join(", "));
      return;
    }
    setShowForm(false);
    setName("");
    setIndustry("");
    setWebsite("");
    setError(null);
    refresh();
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Clients</h1>
          <p className="mt-1 text-sm text-text-muted">Manage your client organisations</p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setError(null);
          }}
          className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={16} /> Add Client
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-text-secondary">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary">Industry</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary">Website</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setError(null);
              }}
              className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {fetching && clients.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">Loading...</p>
      ) : clients.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <p className="text-sm text-text-muted">
            No clients yet. Add your first client to get started.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-card-alt">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Industry
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Website
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Primary Contact
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {clients.map((client) => {
                const primary = client.contacts?.find((c) => c.isPrimary);
                return (
                  <tr key={client.id} className="hover:bg-card-hover">
                    <td className="px-4 py-3">
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium text-accent hover:text-accent-hover"
                      >
                        {client.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {client.industry || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {client.website || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {primary?.name || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
